import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, map, Observable, of, tap } from 'rxjs';

import { ApiUrlService } from '../services/api-url.service';

export interface User {
  id: string;
  email: string;
  role: 'ADMIN' | 'THERAPIST';
}

type AuthStatus = 'unknown' | 'authenticated' | 'unauthenticated';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(ApiUrlService);

  private readonly _user = signal<User | null>(null);
  private readonly _status = signal<AuthStatus>('unknown');

  public readonly user = this._user.asReadonly();
  public readonly status = this._status.asReadonly();

  /**
   * Bootstrap auth state on app initialization
   */
  public bootstrap(): Promise<void> {
    return new Promise((resolve) => {
      this.fetchCurrentUser().subscribe({
        next: () => resolve(),
        error: () => {
          // Try to refresh token if initial fetch fails
          this.refreshToken().subscribe({
            next: (success) => {
              if (success) {
                this.fetchCurrentUser().subscribe({
                  next: () => resolve(),
                  error: () => resolve(),
                });
              } else {
                resolve();
              }
            },
            error: () => resolve(),
          });
        },
      });
    });
  }

  /**
   * Check if user is authenticated
   */
  public isAuthenticated$(): Observable<boolean> {
    if (this._status() === 'unknown') {
      return this.fetchCurrentUser().pipe(
        map(() => this._status() === 'authenticated'),
        catchError(() => of(false)),
      );
    }

    return of(this._status() === 'authenticated');
  }

  /**
   * Login with email and password
   */
  public login(email: string, password: string): Observable<User> {
    return this.http
      .post<{ user: User }>(this.apiUrl.authLogin(), { email, password }, { withCredentials: true })
      .pipe(
        tap(({ user }) => {
          // Check if user has allowed role
          if (user.role !== 'ADMIN' && user.role !== 'THERAPIST') {
            throw new Error('UNAUTHORIZED_ROLE');
          }
          this._user.set(user);
          this._status.set('authenticated');
        }),
        map(({ user }) => user),
      );
  }

  /**
   * Logout and clear session
   */
  public logout(): Observable<void> {
    return this.http.post<void>(this.apiUrl.authLogout(), {}, { withCredentials: true }).pipe(
      tap(() => {
        this._user.set(null);
        this._status.set('unauthenticated');
      }),
      catchError(() => {
        // Clear local state even if API call fails
        this._user.set(null);
        this._status.set('unauthenticated');

        return of(void 0);
      }),
    );
  }

  /**
   * Refresh the access token
   */
  public refreshToken(): Observable<boolean> {
    return this.http
      .post<{ success: boolean }>(this.apiUrl.authRefresh(), {}, { withCredentials: true })
      .pipe(
        map((response) => response.success),
        catchError(() => of(false)),
      );
  }

  /**
   * Get current user from API
   */
  public fetchCurrentUser(): Observable<User> {
    return this.http.get<User>(this.apiUrl.authMe(), { withCredentials: true }).pipe(
      tap((user) => {
        // Check if user has allowed role
        if (user.role !== 'ADMIN' && user.role !== 'THERAPIST') {
          this._user.set(null);
          this._status.set('unauthenticated');
          throw new Error('UNAUTHORIZED_ROLE');
        }
        this._user.set(user);
        this._status.set('authenticated');
      }),
      catchError((error) => {
        this._user.set(null);
        this._status.set('unauthenticated');
        throw error;
      }),
    );
  }

  /**
   * Request password reset
   */
  public forgotPassword(email: string): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      this.apiUrl.authForgotPassword(),
      { email },
      { withCredentials: true },
    );
  }

  /**
   * Validate password reset token
   */
  public validateToken(token: string): Observable<{ valid: boolean; email?: string }> {
    return this.http.post<{ valid: boolean; email?: string }>(
      this.apiUrl.authValidateToken(),
      { token },
      { withCredentials: true },
    );
  }

  /**
   * Set password using token
   */
  public setPassword(token: string, password: string): Observable<User> {
    return this.http
      .post<{ user: User }>(
        this.apiUrl.authSetPassword(),
        { token, password },
        { withCredentials: true },
      )
      .pipe(
        tap(({ user }) => {
          this._user.set(user);
          this._status.set('authenticated');
        }),
        map(({ user }) => user),
      );
  }
}
